/**
 * Settings Page Component
 * Modular settings page with extracted tab components
 */

import React, { useState } from 'react';
import { Settings, Package, Database, DollarSign, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../i18n';
import ProfitSettingsTab from './ProfitSettingsTab';
import { GeneralTab, ProductsTab, DataManagementTab, ExchangeRatesTab } from './tabs';

interface SettingsPageProps {
  onStatsChange?: () => void;
}

type TabId = 'general' | 'products' | 'profit' | 'data' | 'exchange';

const SettingsPage: React.FC<SettingsPageProps> = ({ onStatsChange }) => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabId>('general');

  const tabs = [
    { id: 'general' as const, label: t.settings.general, icon: Settings },
    { id: 'products' as const, label: t.settings.products, icon: Package },
    { id: 'profit' as const, label: language === 'tr' ? 'Operasyonel Maliyetler' : 'Operational Costs', icon: TrendingUp },
    { id: 'data' as const, label: t.dataManagement.title, icon: Database },
    { id: 'exchange' as const, label: t.settings.exchangeRates, icon: DollarSign },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-600 to-slate-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">{t.settings.title}</h2>
            <p className="opacity-90">{t.settings.description}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-slate-200">
          <div className="flex gap-1 p-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'general' && <GeneralTab />}
          {activeTab === 'products' && <ProductsTab onStatsChange={onStatsChange} />}
          {activeTab === 'profit' && <ProfitSettingsTab onSave={() => onStatsChange?.()} />}
          {activeTab === 'data' && <DataManagementTab onStatsChange={onStatsChange} />}
          {activeTab === 'exchange' && <ExchangeRatesTab />}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
